import { useEffect } from 'react';

import { create } from 'zustand';

/**
 * Ref-counted "an overlay is covering the page" signal, used to pause background work
 * behind overlays (`HomeBackground`).
 *
 * `scrollLockStore` can't answer this on its own. It holds a single boolean with no
 * ownership, so one overlay's close clears it even while another is still open — the
 * navbar popover's `onAddFunds` closes the popover (which unlocks 100ms later) and opens
 * the funding dialog in the same tick, and the lock drops for the rest of that form's
 * life. It also misses overlays that never take the lock at all: every DesignSystem3
 * `Modal` (Radix owns its own scroll locking) and a mobile `BottomSheet` (which skips the
 * lock because it covers the viewport anyway).
 *
 * A count of overlay owners survives those handoffs: the receiver acquires before the
 * sender releases, so it never reaches zero in between. Deliberately separate from
 * `scrollLockStore` — acquiring coverage has no DOM side effects, so an overlay can
 * report that it covers the page without taking over body scrolling.
 */
interface OverlayCoverageState {
    /** Number of overlays currently covering the page. */
    count: number;
    actions: {
        acquire: () => void;
        release: () => void;
    };
}

export const useOverlayCoverageStore = create<OverlayCoverageState>()((set) => ({
    count: 0,
    actions: {
        acquire: () => set((state) => ({ count: state.count + 1 })),
        // Clamped so an unbalanced release can't drive the count negative and then
        // swallow a later real overlay's coverage.
        release: () => set((state) => ({ count: Math.max(0, state.count - 1) })),
    },
}));

/**
 * Registers page coverage while `active`, releasing it on cleanup. Overlay primitives call
 * this with their own open state; React's effect teardown keeps acquire/release balanced
 * even when the overlay unmounts while open.
 */
export function useOverlayCoverage(active: boolean): void {
    useEffect(() => {
        if (!active) return;
        const { acquire, release } = useOverlayCoverageStore.getState().actions;
        acquire();
        return release;
    }, [active]);
}

/** Whether any overlay currently covers the page. */
export function useIsPageCoveredByOverlay(): boolean {
    return useOverlayCoverageStore((state) => state.count > 0);
}
